import * as React from 'react';

import { connect } from 'react-redux';

// Store
import AppState from '@webapp/store/state';
import { CloseMenu } from '@webapp/store/ui/action-creators';

import { Translate } from 'react-localize-redux';

// Controllers
import UserAvatarInfo from '@components/basics/user-avatar-info';

// Components
import Flex from '@components/layout/flex';
import NewMenu from '@components/new-menu';
import Button from '@components/actions/button';

import RosterMenuContent from './menu-content';
import { Dispatch } from 'redux';
import {
  CreateRoster,
  RemoveFromRoster,
  UpdateRoster,
} from '@webapp/store/rosters/action-creators';
import { Roster } from '@services/rosters/types/roster';
import SimpleArtist from '@common/types/artist/simple-artist';
import { RosterRelation } from '@services/rosters/types/roster-relation';

interface RosterMenuProps {
  className?: string;
  roster?: Roster;
  labelId?: string;
  avatar?: string;
  fullName?: string;
  relations?: RosterRelation[];
  action: React.ReactNode;
}

interface StateProps {
  isMobile: boolean;
  createRoster: (
    labelId: string,
    rosterName: string,
    artists: SimpleArtist[]
  ) => CreateRoster;
  updateRoster: (roster: Roster, artists?: SimpleArtist[]) => UpdateRoster;
  removeFromRoster: (relation: RosterRelation) => RemoveFromRoster;
  closeMenu: () => CloseMenu;
}

const mapStateToProps = (state: AppState, props: RosterMenuProps) => ({
  avatar: state.recordLabels.active?.avatar,
  fullName: state.recordLabels.active?.name,
  labelId: state.recordLabels.active?.id,
  isMobile: state.ui.mobile,
  relations:
    props.roster && props.roster?.id
      ? state.rosters.artists[props.roster?.id]
      : undefined,
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
  createRoster: (
    labelId: string,
    rosterName: string,
    artists: SimpleArtist[]
  ) => dispatch(new CreateRoster(labelId, rosterName, artists)),
  updateRoster: (roster: Roster, artists?: SimpleArtist[]) =>
    dispatch(new UpdateRoster(roster, artists)),
  removeFromRoster: (relation: RosterRelation) =>
    dispatch(new RemoveFromRoster(relation)),
  closeMenu: () => dispatch(new CloseMenu()),
});

export const RosterMenu: React.FC<RosterMenuProps & StateProps> = ({
  className,
  relations,
  action,
  avatar,
  fullName,
  labelId,
  isMobile,
  createRoster,
  updateRoster,
  removeFromRoster,
  closeMenu,
  ...props
}) => {
  const [isNewRoster, setIsNewRoster] = React.useState(!props.roster?.id);
  const [roster, setRoster] = React.useState<Roster | undefined>(props.roster);
  const [rosterName, setRosterName] = React.useState(props.roster?.name);
  const [showError, setShowError] = React.useState(false);
  const [selectedArtists, setSelectedArtists] = React.useState<SimpleArtist[]>(
    []
  );
  const [artistsToRemove, setArtistsToRemove] = React.useState<
    RosterRelation[]
  >([]);

  React.useEffect(() => {
    if (roster?.id) {
      setIsNewRoster(false);
    }
  }, [roster, setIsNewRoster]);

  const handleSave = async () => {
    if (!rosterName) {
      return null;
    }

    if (isNewRoster && labelId) {
      createRoster(labelId, rosterName, selectedArtists);
    } else if (roster) {
      const updatedRoster = {
        ...roster,
        name: rosterName,
      };
      updateRoster(updatedRoster, selectedArtists);
      setRoster(updatedRoster);
    }

    artistsToRemove.forEach((a) => {
      removeFromRoster(a);
    });
    closeMenu();
  };

  return (
    <NewMenu
      className={className}
      button={action}
      menuId={isNewRoster ? 'NEW_ROSTER' : `ROSTER-${roster?.id}`}
      header={
        <>
          {<UserAvatarInfo className="mb-30" src={avatar} name={fullName!} />}
        </>
      }
      footer={
        <Flex gap={5}>
          <Button height={60} buttonType="secondary" onClick={closeMenu}>
            <Translate id="USER_PROFILE.ROSTER.MENU.BUTTON.DISCARD" />
          </Button>
          <Button
            disabled={!rosterName}
            height={60}
            buttonType="primary"
            onClick={handleSave}
          >
            <Translate id="USER_PROFILE.ROSTER.MENU.BUTTON.SAVE" />
          </Button>
        </Flex>
      }
    >
      <RosterMenuContent
        isNewRoster={isNewRoster}
        rosterId={roster?.id}
        activelabelId={labelId}
        rosterName={rosterName}
        rosterArtists={relations}
        setRosterName={setRosterName}
        showError={showError}
        setShowError={setShowError}
        addNewRosterArtists={setSelectedArtists}
        addExistingRosterArtist={setSelectedArtists}
        removeRosterArtist={setArtistsToRemove}
        onMenuClose={closeMenu}
        formatter={(value) => value.toLowerCase().split('.')[0].trim()}
      />
    </NewMenu>
  );
};

export default connect(mapStateToProps, mapDispatchToProps)(RosterMenu);
